import { useState, useEffect, useRef } from 'react';

const useGeolocation = ({ enabled = true, timeout = 10000 }) => {
  const [country, setCountry] = useState(null);
  const [countryCode, setCountryCode] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    mountedRef.current = true;
    const abortController = new AbortController();

    const getCountry = async (latitude, longitude) => {
      try {
        // Check cache first (using localStorage for cross-tab sharing)
        const cacheKey = `geolocation_hook:nominatim_${latitude.toFixed(4)}_${longitude.toFixed(4)} `;
        const cached = localStorage.getItem(cacheKey);

        if (cached) {
          const cachedData = JSON.parse(cached);
          if (Date.now() - cachedData.timestamp < 24 * 60 * 60 * 1000) { // 24 hours
            if (mountedRef.current) {
              setCountry(cachedData.country);
              setCountryCode(cachedData.countryCode || null);
              setLoading(false);
            }
            return;
          }
        }

        // Add email parameter for identification (User-Agent header doesn't work in browsers)
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&email=naturespulsee@gmail.com`,
          {
            headers: {
              'Accept': 'application/json'
            },
            signal: abortController.signal
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (mountedRef.current) {
          if (data.address && data.address.country) {
            setCountry(data.address.country);
            setCountryCode(data.address.country_code ? data.address.country_code.toUpperCase() : null);

            // Cache the result
            localStorage.setItem(cacheKey, JSON.stringify({
              country: data.address.country,
              countryCode: data.address.country_code ? data.address.country_code.toUpperCase() : null,
              timestamp: Date.now()
            }));
          } else {
            setError('Country not found in response.');
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError' && mountedRef.current) {
          console.error('Geocoding error:', err);
          setError(`Failed to fetch country: ${err.message}`);
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (mountedRef.current) {
            getCountry(position.coords.latitude, position.coords.longitude);
          }
        },
        (err) => {
          if (mountedRef.current) {
            console.error('Geolocation error:', err);
            setError(`Geolocation error: ${err.message}`);
            setLoading(false);
          }
        },
        {
          enableHighAccuracy: false, // Reduces battery usage
          timeout: timeout, // Use the passed timeout
          maximumAge: 300000 // Accept 5-minute old position
        }
      );
    } else {
      if (mountedRef.current) {
        setError('Geolocation is not supported by this browser.');
        setLoading(false);
      }
    }

    // Cleanup function
    return () => {
      mountedRef.current = false;
      abortController.abort();
    };
  }, [enabled, timeout]); // Add enabled and timeout to dependency array

  return { country, countryCode, error, loading };
};

export default useGeolocation;
